// Calendar rules for manual abos, shared by the server (billing run) and the client (default
// "next billing date" in the form) — one rule, not two copies that drift apart.
//
// A CALENDAR DAY is a Date at 00:00 UTC. Billing dates are days, not instants: the containers run
// in UTC, the operator sits in Cyprus, and a local-midnight Date would slide a day depending on
// who computes it. Everything here reads and writes the UTC fields only.

export type AboCadence = 'monthly' | 'yearly'

// The placeholders a product name may carry — the spelling of the old Ninox system, so imported
// names work unchanged. Shown as a hint in the form.
export const ABO_PLACEHOLDER_START = '{$Abo-Start}'
export const ABO_PLACEHOLDER_END = '{$Abo-Ende}'
export const ABO_PLACEHOLDERS = [ABO_PLACEHOLDER_START, ABO_PLACEHOLDER_END]

const MS_PER_DAY = 24 * 60 * 60 * 1000

export function calendarDay(year: number, monthIdx: number, day: number): Date {
    return new Date(Date.UTC(year, monthIdx, day))
}

// The UTC calendar day of an instant (e.g. `new Date()` → today).
export function toCalendarDay(d: Date): Date {
    return calendarDay(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
}

// 'YYYY-MM-DD' (date input, ISO string) → calendar day; undefined for anything else.
export function parseCalendarDay(v: unknown): Date | undefined {
    if (v instanceof Date) return isNaN(v.getTime()) ? undefined : toCalendarDay(v)
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v ?? ''))
    if (!m) return undefined
    const d = calendarDay(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    return d.getUTCDate() === Number(m[3]) ? d : undefined // rejects 2026-02-31
}

export function isoDay(d: Date): string {
    return d.toISOString().slice(0, 10)
}

export function formatDayDe(d: Date): string {
    const dd = String(d.getUTCDate()).padStart(2, '0')
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0')
    return `${dd}.${mm}.${d.getUTCFullYear()}`
}

export function daysInMonth(year: number, monthIdx: number): number {
    return new Date(Date.UTC(year, monthIdx + 1, 0)).getUTCDate()
}

function isLastDayOfMonth(d: Date): boolean {
    return d.getUTCDate() === daysInMonth(d.getUTCFullYear(), d.getUTCMonth())
}

// The start of the NEXT period. The day is the abo's `billingDay`, capped to the month's length,
// so a 31st becomes the 28th/29th in February and the 31st again in March — it never slides to
// the 28th for good (Ninox's date(y, m+1, d) had no such anchor).
export function addPeriod(start: Date, cadence: AboCadence, billingDay: number): Date {
    let y = start.getUTCFullYear()
    let m = start.getUTCMonth()
    if (cadence === 'monthly') { m += 1; if (m > 11) { m = 0; y += 1 } } else { y += 1 }
    return calendarDay(y, m, Math.min(billingDay, daysInMonth(y, m)))
}

// Last day of the period that starts at `start`: the next period's start minus one day, so the
// periods join without gap or overlap (10.09. → 09.10.; 31.01. → 27.02.).
export function periodEnd(start: Date, cadence: AboCadence, billingDay: number): Date {
    return new Date(addPeriod(start, cadence, billingDay).getTime() - MS_PER_DAY)
}

// The first date in the abo's rhythm on or after `today`, stepping on from `from`. An abo that
// continues WITHOUT back-billing lands here: reactivation (operator, 02.10.2026) and the one-off
// migration of manual abos that had already run (scripts/migrate-manual-abos.ts). A date already
// on or after `today` stays as it is.
export function firstDueOnOrAfter(from: Date, cadence: AboCadence, billingDay: number, today: Date): Date {
    let next = toCalendarDay(from)
    while (next < today) next = addPeriod(next, cadence, billingDay)
    return next
}

// The billing day after the next date was set by hand (form, import): it follows the new date —
// EXCEPT when that date is a capped month end of the current anchor (billing day 31, date
// 28.02.), which must not throw the anchor away.
export function billingDayFor(next: Date, previous?: number): number {
    const d = next.getUTCDate()
    if (previous && previous > d && isLastDayOfMonth(next)) return previous
    return d
}

// The cut-off day of the automatic run: the last month end on or before today. On the 30.09. it
// is the 30.09.; a retry on the 01.10. keeps the 30.09.
export function lastMonthEndOnOrBefore(today: Date): Date {
    if (isLastDayOfMonth(today)) return today
    return new Date(calendarDay(today.getUTCFullYear(), today.getUTCMonth(), 1).getTime() - MS_PER_DAY)
}

export function fillAboPlaceholders(template: string, start: Date, end: Date): string {
    return template
        .split(ABO_PLACEHOLDER_START).join(formatDayDe(start))
        .split(ABO_PLACEHOLDER_END).join(formatDayDe(end))
}
